import { useEffect, useId, useRef, useState } from 'react'
import { ButtonGroup, IconButton } from '../components/ui'
import { suppressTooltipsBriefly } from '../components/ui/Tooltip'
import {
  getThemePrefs, setDensity, setShape, setThemeMode,
  type Density, type ShapePref, type ThemeMode,
} from '../theme/theme'

const MODES: Array<{ value: ThemeMode; label: string; icon: string }> = [
  { value: 'light', label: 'Chiaro', icon: 'light_mode' },
  { value: 'dark', label: 'Scuro', icon: 'dark_mode' },
  { value: 'auto', label: 'Auto', icon: 'contrast' },
]
const SHAPES: Array<{ value: ShapePref; label: string; icon: string }> = [
  { value: 'soft', label: 'Morbidi', icon: 'rounded_corner' },
  { value: 'sharp', label: 'Netti', icon: 'square' },
]

const DENSITIES: Array<{ value: string; label: string }> = [
  { value: '0', label: '0' },
  { value: '-1', label: '−1' },
  { value: '-2', label: '−2' },
  { value: '-3', label: '−3' },
]

/** Side sheet M3 dentro <dialog>: focus trap, Esc e ritorno del focus li gestisce il browser. */
export function ThemePanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const [prefs, setPrefs] = useState(getThemePrefs)

  useEffect(() => {
    const el = ref.current; if (!el) return
    if (open && !el.open) { setPrefs(getThemePrefs()); suppressTooltipsBriefly(); el.showModal() }
    else if (!open && el.open) { suppressTooltipsBriefly(); el.close() }
  }, [open])

  function chooseMode(m: ThemeMode) { setThemeMode(m); setPrefs(getThemePrefs()) }
  function chooseShape(s: ShapePref) { setShape(s); setPrefs(getThemePrefs()) }
  function chooseDensity(v: string) { setDensity(Number(v) as Density); setPrefs(getThemePrefs()) }

  return (
    <dialog
      ref={ref}
      className="md-sheet"
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="md-sheet__body">
        <div className="md-sheet__head">
          <h2 id={titleId} className="md-sheet__title">Personalizza</h2>
          <IconButton icon="close" aria-label="Chiudi" onClick={onClose} />
        </div>

        <fieldset className="md-sheet__group">
          <legend>Modalità</legend>
          <ButtonGroup
            aria-label="Modalità"
            options={MODES}
            value={prefs.mode}
            onChange={v => chooseMode(v as ThemeMode)}
          />
        </fieldset>

        <fieldset className="md-sheet__group">
          <legend>Bordi</legend>
          <ButtonGroup
            aria-label="Bordi"
            options={SHAPES}
            value={prefs.shape}
            onChange={v => chooseShape(v as ShapePref)}
          />
        </fieldset>

        <fieldset className="md-sheet__group">
          <legend>Densità</legend>
          <ButtonGroup
            aria-label="Densità"
            options={DENSITIES}
            value={String(prefs.density)}
            onChange={chooseDensity}
          />
          <p className="md-sheet__hint">Altezza di campi, righe delle tabelle e voci di lista: 0 è la più ampia, −3 la più compatta.</p>
        </fieldset>
      </div>
    </dialog>
  )
}
